import { useEffect, useMemo, useState } from "react";

import { useIsFetching, useQuery } from "@tanstack/react-query";
import { useSandboxStateStore } from "@v2/store/SandboxStateStoreContext";
import { nextJSProjectCacheRouter } from "@v2/trpc/routes/nextJSProjectCache";
import { stripAllScriptTags } from "@v2/utils/dom";
import { transformNextImageToImg } from "replo-utils/lib/html";
import {
  getLayoutCssUrl,
  getPageHtmlUrl,
} from "replo-utils/lib/nextJSProjectCache";

export const useAutoSaveHtmlContent = ({
  projectId,
}: {
  projectId?: string;
}) => {
  const { mutate: savePageHtml } =
    nextJSProjectCacheRouter.savePageHtml.useMutation();
  const { mutate: saveLayoutCss } =
    nextJSProjectCacheRouter.saveLayoutCss.useMutation();

  const overrideStyleLibrary = useSandboxStateStore(
    (state) => state.currentStyleLibrary,
  );
  const viewingVersionId = useSandboxStateStore(
    (state) => state.viewingVersionId,
  );

  // Note (Noah, 2025-10-29): Any time we're NOT viewing a specific version or overriding
  // anything, we should make sure the dev server understands that we should cache the HTML
  // content when it's emitted. If we unmount, that means the sandbox is no longer in a specific
  // state, so that's also fine to cache the emitted content since there are no overrides.
  const shouldSaveHtmlContent = !viewingVersionId && !overrideStyleLibrary;

  // Always capture emitted content
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!projectId) {
        return;
      }

      if (event.data.type === "emitHTML" && event.data.htmlContent) {
        const transformedHtml = stripAllScriptTags(event.data.htmlContent);
        if (isUncacheableRender(transformedHtml)) {
          return;
        }

        if (projectId && shouldSaveHtmlContent) {
          void savePageHtml({
            projectId,
            pageRoute: event.data.pageRoute,
            html: transformedHtml,
          });
          void saveLayoutCss({
            projectId,
            css: event.data.cssContent,
          });
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, [projectId, savePageHtml, saveLayoutCss, shouldSaveHtmlContent]);
};

async function getCachedPageContent({
  projectId,
  pageRoute,
}: {
  projectId: string;
  pageRoute: string;
}) {
  const url = getPageHtmlUrl(projectId, pageRoute);
  const response = await fetch(url);
  if (!response.ok) {
    if (response.status === 404) {
      return "";
    }
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(`Failed to fetch page content from ${url}`);
  }
  const html = await response.text();
  return html;
}

async function getCachedLayoutCss({ projectId }: { projectId: string }) {
  const url = getLayoutCssUrl(projectId);
  const response = await fetch(url);
  if (!response.ok) {
    if (response.status === 404) {
      return "";
    }
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(`Failed to fetch layout css from ${url}`);
  }
  const css = await response.text();
  return css;
}

const CACHED_PAGE_CONTENT_QUERY_KEY = "cachedPageContent";

/**
 * True when a render must never be cached as a real page, and — if it's already
 * in the cache — must be treated as a miss so the live preview can re-render and
 * the cache can self-heal.
 *
 * This guards both the write path (don't cache a bad render) and the read path
 * (don't serve a poisoned entry). We detect:
 *   1. The scaffold's own not-found.tsx / error.tsx, via the explicit
 *      `data-replo-page-status` marker they render. This is the durable signal —
 *      it doesn't depend on user-visible copy, so it survives copy changes. New
 *      sandboxes (built from a snapshot that includes the marker) hit this.
 *   2. Next.js's built-in not-found page ("This page could not be found.").
 *   3. The scaffold's current custom not-found copy. This is a fallback for
 *      sandboxes built before the marker shipped, so their already-poisoned
 *      cache entries still self-heal on read.
 *
 * NOTE: do NOT key off the Next dev overlay host (`<nextjs-portal>`). Previews
 * always run `next dev`, and that element is present in the post-hydration
 * `outerHTML` of EVERY page (healthy or not), so matching it would reject all
 * healthy renders on write and evict all healthy cache on read.
 *
 * NOTE: scripts must be stripped before calling this — Next inlines the
 * "could not be found" string in a script object even on healthy pages.
 */
function isUncacheableRender(html: string) {
  return (
    html.includes("data-replo-page-status") ||
    html.includes("This page could not be found.") ||
    html.includes("The page you are looking for does not exist")
  );
}

/**
 * This is a hook that can be used to get the static html content for a page.
 * Data will only be returned if this page has previously been rendered for this
 * project.
 *
 * NOTE (Gabe 2025-08-15): This hook doesn't output html content until all
 * active instances of this hook have returned data. This is to prevent a list
 * of previews from rendering 1 by 1, and instead have them all render at once.
 */
export const useCachedPageContent = ({
  pageRoute,
  projectId,
}: {
  pageRoute: string;
  projectId?: string;
}): { isLoading: boolean; html: string | undefined } => {
  // NOTE (Gabe 2025-08-15): Because we block returning of data if any cached
  // page content query is fetching, we need to use hasReturned to ensure that
  // if this instance of the hook has ever returned data then it will continue
  // to return data. Otherwise, a list of previews that were previously rendered
  // will be hidden by a subsequent instance of the hook that starts fetching.
  const [hasReturned, setHasReturned] = useState(false);

  const isAnyCachedContentFetching =
    useIsFetching({ queryKey: [CACHED_PAGE_CONTENT_QUERY_KEY] }) > 0;

  const { data: html, isLoading: isLoadingCachedPage } = useQuery({
    staleTime: 1000 * 10,
    queryKey: [CACHED_PAGE_CONTENT_QUERY_KEY, projectId, pageRoute],
    queryFn: async () => {
      if (!projectId) {
        return undefined;
      }

      const [css, html] = await Promise.all([
        getCachedLayoutCss({ projectId }),
        getCachedPageContent({ projectId, pageRoute }),
      ]);
      let transformedHtml = html;
      if (html) {
        transformedHtml = stripAllScriptTags(html);
      }
      if (isUncacheableRender(transformedHtml)) {
        return "";
      }
      if (!css || !transformedHtml) {
        return "";
      }

      transformedHtml = transformNextImageToImg(
        html.replace(
          /<link rel="stylesheet" href="([^"]+)"[^>]*>/,
          `<style>${css}</style>`,
        ),
      );

      return transformedHtml;
    },
    enabled: Boolean(projectId),
    retry: false,
    refetchOnWindowFocus: false,
    // A laptop waking from sleep reports "online" before the network works, so
    // refetching here fails once per preview card in tabs left open for days.
    refetchOnReconnect: false,
    // A failed cache read leaves no html, so PageRenderer falls back to the
    // live iframe; an "Unknown error" toast per preview card would be noise.
    meta: { reploIgnoreDefaultErrorHandling: true },
  });
  // NOTE (Gabe 2025-08-15): There is a race condition where
  // isAnyCachedContentFetching can be false but data is not actually available
  // for this page.
  const isLoading = isLoadingCachedPage || isAnyCachedContentFetching;

  const returnValue = useMemo(() => {
    if (!isLoading || hasReturned) {
      return { html, isLoading: false };
    }
    return { html: undefined, isLoading: true };
  }, [hasReturned, html, isLoading]);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  useEffect(() => {
    if (returnValue) {
      setHasReturned(true);
    }
  }, [returnValue]);

  return returnValue;
};
